"use client";

import { useCallback, useEffect, useRef, useState } from "react";

// Reading progress belongs to one open document, never to a previous opening
// or another document. Reaching the end does not itself grant consent.
export function useReadToEnd(active: boolean, contentKey: string = "") {
  const body = useRef<HTMLDivElement>(null);
  const [progress, setProgress] = useState({ active, contentKey, reachedEnd: false });
  // Reset before rendering controls so another document cannot reuse completion
  // while waiting for its scroll observer to attach.
  if (progress.active !== active || progress.contentKey !== contentKey) {
    setProgress({ active, contentKey, reachedEnd: false });
  }

  const checkEnd = useCallback(() => {
    const element = body.current;
    if (active && element && element.clientHeight > 0 && element.scrollHeight - element.clientHeight - element.scrollTop <= 2) {
      setProgress(current => current.active === active && current.contentKey === contentKey && !current.reachedEnd
        ? { ...current, reachedEnd: true } : current);
    }
  }, [active, contentKey]);

  useEffect(() => {
    const element = body.current;
    if (!active || !element) return;
    element.scrollTop = 0;
    const frame = requestAnimationFrame(checkEnd);
    const observer = new ResizeObserver(checkEnd);
    observer.observe(element);
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [active, contentKey, checkEnd]);

  return { body, checkEnd, reachedEnd: progress.active === active && progress.contentKey === contentKey && progress.reachedEnd };
}
